import { observable, computed, action } from "mobx";

import { CommonStoreStatus } from "./CommonStoreStatus";

import { Event, IdentityRole, AccreditationLevel, GenericIdentityGroup } from "../model/Event";
import { ChildCreationResult } from "../model/ChildCreationResult";
import { PatchRequest } from "../requests/PatchRequest";
import { IdentityRoleRequest } from "../requests/EventRequest";

import EventEndpoints from "../endpoints/EventEndpoints";

import { LoadingManager } from "../context";
import { GenericIdentityGroupRequest } from "../requests/GenericIdentityGroupRequest";

export interface EventStoreState {
    status: CommonStoreStatus;
    event: Event;
}

export class EventStore {
    private eventId: number;
    private loadingManager: LoadingManager;

    @observable private state: EventStoreState = {
        status: CommonStoreStatus.LOADING,
        event: null
    };

    constructor(eventId: number, loadingManager: LoadingManager) {
        this.eventId = eventId;
        this.loadingManager = loadingManager;
    }

    destroy() {
        // TODO: determine if there's a need to clear things to
        // avoid memory leaks
    }

    @action refresh(): Promise<Event> {
        this.loadingManager.startLoading();
        return new Promise((resolve, reject) => {
            EventEndpoints
                .getEvent(this.eventId)
                .then(event => {
                    this.state.event = event;
                    this.state.status = CommonStoreStatus.READY;
                    this.loadingManager.doneLoading();
                    resolve(event);
                })
                .catch(err => {
                    console.error("Error in EventStore", err);
                    this.state.status = CommonStoreStatus.ERROR;
                    this.loadingManager.doneLoading();
                    reject(err);
                });
        });
    }

    @action updateField(field: string, value: string): Promise<Event> {
        return this.standardConsumeEndpoint(() =>
            EventEndpoints.editEvent(this.eventId, { field, value }));
    }

    // --- LOCATIONS ---

    @action addEventLocation(name: string): Promise<Location> {
        return this.childCreationEndpoint(() => EventEndpoints.addEventLocation(this.eventId, { name }));
    }

    @action editEventLocation(locationId: number, request: PatchRequest): Promise<Event> {
        return this.standardConsumeEndpoint(() =>
            EventEndpoints.editEventLocation(locationId, request));
    }

    @action deleteEventLocation(locationId: number): Promise<Event> {
        return this.standardConsumeEndpoint(() => EventEndpoints.deleteEventLocation(locationId));
    }

    // --- POSITIONS ---

    @action addEventPosition(name: string): Promise<Position> {
        return this.childCreationEndpoint(() => EventEndpoints.addEventPosition(this.eventId, { name }));
    }

    @action editEventPosition(positionId: number, request: PatchRequest): Promise<Event> {
        return this.standardConsumeEndpoint(() => EventEndpoints.editEventPosition(positionId, request));
    }

    @action deleteEventPosition(locationId: number): Promise<Event> {
        return this.standardConsumeEndpoint(() => EventEndpoints.deleteEventPosition(locationId));
    }

    // --- ACCREDITATION LEVELS ---

    @action addEventAccreditationLevel(name: string, badgeText: string): Promise<AccreditationLevel> {
        return this.childCreationEndpoint(() => EventEndpoints.addEventAccreditationLevel(this.eventId, { name, badgeText }));
    }

    @action editEventAccreditationLevel(accreditationId: number, request: PatchRequest): Promise<Event> {
        return this.standardConsumeEndpoint(() => EventEndpoints.editEventAccreditationLevel(accreditationId, request));
    }

    @action deleteEventAccreditationLevel(accreditationId: number): Promise<Event> {
        return this.standardConsumeEndpoint(() => EventEndpoints.deleteEventAccreditationLevel(accreditationId));
    }

    // --- ROLES ---

    @action addEventRole(request: IdentityRoleRequest): Promise<IdentityRole> {
        return this.childCreationEndpoint(() => EventEndpoints.addEventRole(this.eventId, request));
    }

    @action editEventRole(roleId: number, request: IdentityRoleRequest): Promise<Event> {
        return this.standardConsumeEndpoint(() =>
            EventEndpoints.editEventRole(roleId, request));
    }

    @action deleteEventRole(roldId: number): Promise<Event> {
        return this.standardConsumeEndpoint(() =>
            EventEndpoints.deleteEventRole(roldId));
    }

    // --- GENERIC IDENTITY GROUPS ---

    @action addGenericIdentityGroup(request: GenericIdentityGroupRequest): Promise<GenericIdentityGroup> {
        return this.childCreationEndpoint(() => EventEndpoints.addGenericIdentityGroup(this.eventId, request));
    }

    @action editGenericIdentityGroup(groupId: number, request: GenericIdentityGroupRequest): Promise<Event> {
        return this.standardConsumeEndpoint(() =>
            EventEndpoints.editGenericIdentityGroup(groupId, request));
    }

    @action archiveGenericIdentityGroup(groupId: number): Promise<Event> {
        return this.standardConsumeEndpoint(() =>
            EventEndpoints.archiveGenericIdentityGroup(groupId));
    }

    @action unarchiveGenericIdentityGroup(groupId: number): Promise<Event> {
        return this.standardConsumeEndpoint(() =>
            EventEndpoints.unarchiveGenericIdentityGroup(groupId));
    }


    @computed get status(): CommonStoreStatus {
        return this.state.status;
    }

    @computed get event(): Event {
        return this.state.event;
    }

    private standardConsumeEndpoint(logic: () => Promise<Event>): Promise<Event> {
        return new Promise((resolve, reject) => {
            logic()
                .then(event => {
                    this.state.event = event;
                    resolve(event);
                })
                .catch(err => {
                    console.error("Error in EventStore", err);
                    reject(err);
                });
        });
    }

    private childCreationEndpoint<C>(logic: () => Promise<ChildCreationResult<Event, C>>): Promise<C> {
        return new Promise((resolve, reject) => {
            logic()
                .then(response => {
                    this.state.event = response.parent;
                    resolve(response.child);
                })
                .catch(err => {
                    console.error("Error in EventStore", err);
                    reject(err);
                });
        });
    }
}
